import * as React from "react"
import { Sections } from "../work/sections"
import { ABOUT } from "./content"
import { CARD, frame as timelineFor, scene, type Variant } from "./motion"
import { DeckGL, type CardArt } from "./gl"
import { progress } from "../home/motion"
import { glint } from "./light"
import { Foil } from "./foil"
import "./about.css"

/** About: a full-screen photo (a solid colour for now) shrinks into a card; the
 *  deck fans out in 3D, closes again showing the capabilities on the cards'
 *  backs, and the card on top turns into a modal with a get-in-touch form.
 *  Scrolling drives it: the page is tall, the scene stays on screen, and the
 *  scroll position is the time on the timeline (src/about/motion.ts). */
/** The intro text: lines split by \n, paragraphs by a blank line, word by word. */
export function IntroText({ text, className }: { text: string; className: string }) {
    return (
        <p className={className}>
            {text.split("\n\n").map((para, k) => (
                <span key={k} className="ab-para">
                    {para.split("\n").map((line, i) => (
                        <React.Fragment key={i}>
                            {i > 0 && <br />}
                            {line.split(" ").map((word, j) => (
                                <React.Fragment key={j}>
                                    {(i > 0 || j > 0) && " "}
                                    <span className="ab-w">
                                        <span>{word}</span>
                                    </span>
                                </React.Fragment>
                            ))}
                        </React.Fragment>
                    ))}
                </span>
            ))}
        </p>
    )
}

/** The photo comes in once it's ready (decoded): fading in while settling from
 *  a little larger to its size (about.css). Until then its card is black. */
function photoIn(img: HTMLImageElement) {
    if (img.dataset.in != null) return
    img.dataset.in = ""
    const show = () => img.setAttribute("data-shown", "")
    ;(img.decode ? img.decode() : Promise.resolve()).then(show, show)
}

/** WebGL 2 here? (/aabout2 is drawn as a real 3D scene with it; without, as the page's cards) */
let glOk: boolean | null = null
const canGL = () => {
    if (glOk === null) {
        try {
            glOk = !!document.createElement("canvas").getContext("webgl2")
        } catch {
            glOk = false
        }
    }
    return glOk
}

/** /aabout's collectible card, front (Figma "Frame 975"): a holographic panel,
 *  "collectible skill", the skill's name in chrome, its rarity and number. The
 *  sheen and glare move with the card's turn (--sh, --gx: set per frame). */
function SkillFront({ skill }: { skill: { title: string; no: string; rarity: string } }) {
    return (
        <>
            <div className="hc-ring" />
            <div className="hc-body" />
            <div className="hc-foil" />
            <canvas className="hc-gl" aria-hidden="true" />
            <div className="hc-panel">
                <div className="hc-rainbow" />
                <div className="hc-beams" />
                <div className="hc-glitter" />
            </div>
            <span className="hc-label">Collectible skill</span>
            <h3 className="hc-title">{skill.title}</h3>
            <span className="hc-rarity">{skill.rarity}</span>
            <span className="hc-no">{skill.no}</span>
            <div className="hc-spec" />
        </>
    )
}

/** its back (Figma "Frame 976"): the NTO mark, highly reflective; the card itself only a little */
function MarkBack() {
    return (
        <>
            <div className="hc-ring" />
            <div className="hc-body" />
            <div className="hc-foil" />
            <canvas className="hc-gl" aria-hidden="true" />
            <div className="hc-mark" />
            <div className="hc-spec hc-spec-soft" />
        </>
    )
}

/** a capability on a card: its name at the top, a line under it */
function Capability({ c, low }: { c: { title: string; text: string }; low?: boolean }) {
    return (
        <div className={"ab-cap" + (low ? " ab-cap-low" : "")}>
            <h3>{c.title}</h3>
            <p>{c.text}</p>
        </div>
    )
}

export default function About({ variant = "deck" }: { variant?: Variant }) {
    const root = React.useRef<HTMLDivElement>(null)
    const stage = React.useRef<HTMLDivElement>(null)
    const cards = React.useRef<(HTMLDivElement | null)[]>([])
    const modal = React.useRef<HTMLDivElement>(null)
    const modalIn = React.useRef<HTMLDivElement>(null)
    const intro = React.useRef<HTMLDivElement>(null)
    const blurNodes = React.useRef<(SVGFEGaussianBlurElement | null)[]>([])
    // a card's blur: its motion blur (desktop) and the soft blur of the fly-in
    const motion = React.useRef<boolean[]>([])
    const soft = React.useRef<number[]>([])
    const filterOf = (i: number) => {
        const parts = []
        if (motion.current[i]) parts.push(`url(#ab-blur-${i})`)
        if (soft.current[i] > 0.05) parts.push(`blur(${soft.current[i].toFixed(2)}px)`)
        const el = cards.current[i]
        if (el) el.querySelectorAll<HTMLElement>(".ab-face").forEach((face) => set(face, "filter", parts.join(" ") || "none"))
    }
    const deck = ABOUT.cards.filter((c) => variant === "fly" || !c.fly)
    // /aabout2: one canvas, a real 3D scene (src/about/gl.ts)
    const use3d = variant === "fly" && canGL()
    const canvas = React.useRef<HTMLCanvasElement>(null)
    const gl = React.useRef<DeckGL | null>(null)
    React.useLayoutEffect(() => {
        if (!use3d || !canvas.current) return
        const art: CardArt[] = deck.map((c) => ({
            photo: c.photo,
            front: { color: c.color, ink: c.ink, title: c.front?.title, text: c.front?.text, low: true },
            back: { color: c.color, ink: c.ink, title: c.back?.title, text: c.back?.text },
        }))
        gl.current = DeckGL.create(canvas.current, art, () => innerWidth > innerHeight)
        return () => {
            gl.current?.dispose()
            gl.current = null
        }
    }, [])
    const [sent, setSent] = React.useState(false)
    // the holographic foil: one WebGL context drawing every card's (src/about/foil.ts)
    const foil = React.useRef<Foil | null>(null)
    const last = React.useRef(0)
    React.useLayoutEffect(() => {
        if (variant !== "deck") return
        foil.current = Foil.create(() => paint(last.current))
        if (foil.current) root.current?.setAttribute("data-foil", "")
        return () => {
            foil.current = null
        }
    }, [])

    // only what changed is written (sizes change only while the photo shrinks
    // and the modal grows: everything else moves by transform alone)
    const written = React.useRef(new WeakMap<HTMLElement, Record<string, string>>())
    const set = (el: HTMLElement, prop: string, value: string) => {
        let w = written.current.get(el)
        if (!w) written.current.set(el, (w = {}))
        if (w[prop] === value) return
        w[prop] = value
        if (prop.startsWith("data-")) el.setAttribute(prop, value)
        else el.style.setProperty(prop, value)
    }
    const px = (v: number) => Math.round(v * 10) / 10 + "px"

    const paint = React.useCallback((time: number) => {
        const r = root.current
        if (!r) return null
        last.current = time
        if (gl.current) {
            // the 3D scene: the canvas draws the cards, the page the text and the modal
            const sc = scene(time, innerWidth, innerHeight)
            set(r, "--s", String(sc.scale))
            set(r, "data-axis", sc.axis)
            gl.current.show(sc)
            set(intro.current!, "opacity", sc.intro.toFixed(3))
            set(intro.current!, "visibility", sc.intro > 0 ? "visible" : "hidden")
            const m = sc.modal
            const me = modal.current!
            set(me, "visibility", m.visible ? "visible" : "hidden")
            if (m.visible) {
                set(me, "width", px(m.w))
                set(me, "height", px(m.h))
                set(me, "border-radius", px(m.r))
                set(me, "transform", `translate3d(-50%, -50%, 0) scale(${m.scale.toFixed(4)})`)
                set(me, "opacity", m.opacity.toFixed(3))
                set(modalIn.current!, "opacity", m.content.toFixed(3))
                set(modalIn.current!, "pointer-events", m.content > 0.9 ? "auto" : "none")
            }
            return null
        }
        const f = timelineFor(time, innerWidth, innerHeight, variant)
        set(r, "--s", String(f.scale))
        set(r, "--k", String((f.scale * CARD.w) / 404))
        set(r, "data-axis", f.axis)
        set(r, "data-depth", f.depth ? "true" : "false")
        f.cards.forEach((c, i) => {
            const el = cards.current[i]
            if (!el) return
            set(el, "visibility", c.visible ? "visible" : "hidden")
            if (!c.visible) return
            set(el, "width", px(c.w))
            set(el, "height", px(c.h))
            set(el, "border-radius", px(c.r))
            set(el, "z-index", String(c.layer))
            // the light reflecting off it, as in a mirror (src/about/light.ts): where
            // it shows across the card, how bright, and the hologram's colour shift
            const g = glint(c.rx, c.persp, ((f.axis === "x" ? c.w : c.h) * c.scale) / 2)
            set(el, "--hl", (g.at * 100).toFixed(2) + "%")
            set(el, "--hn", g.at.toFixed(4))
            set(el, "--hi", g.strength.toFixed(3))
            set(el, "--hue", (g.hue * 4).toFixed(1) + "deg")
            set(el, "--mk", c.mark.toFixed(3))
            // (on its two sides: an opacity on the card itself would flatten them into one)
            el.querySelectorAll<HTMLElement>(".ab-face").forEach((face) => set(face, "opacity", c.opacity.toFixed(3)))
            if (foil.current) {
                const v = { w: c.w * c.scale, h: c.h * c.scale, rx: c.rx, axis: f.axis, cx: c.x - c.cx, cy: c.y - c.cy, z: c.z, persp: c.persp, mark: c.mark }
                el.querySelectorAll<HTMLCanvasElement>(".hc-gl").forEach((cv) => {
                    const back = !!cv.closest(".ab-back")
                    if (Foil.facing(v, back)) foil.current!.draw({ canvas: cv, back }, v)
                })
            }
            // its own camera, in front of its centre (as Jitter's Card Flip)
            const turn = f.axis === "x" ? `rotateY(${(-c.rx).toFixed(3)}deg)` : `rotateX(${c.rx.toFixed(3)}deg)`
            // seen by its camera (cx, cy; moved forward by z): in front of its own
            // centre until the fly-in (/aabout2) joins them into one
            set(
                el,
                "transform",
                `translate3d(-50%, -50%, 0) translate(${c.cx.toFixed(2)}px, ${c.cy.toFixed(2)}px) perspective(${c.persp.toFixed(1)}px) translateZ(${c.z.toFixed(2)}px) translate(${(c.x - c.cx).toFixed(2)}px, ${(c.y - c.cy).toFixed(2)}px) ${turn} scale(${c.scale.toFixed(4)})`
            )
            soft.current[i] = c.blur
            filterOf(i)
        })
        set(intro.current!, "opacity", f.intro.toFixed(3))
        set(intro.current!, "visibility", f.intro > 0 ? "visible" : "hidden")
        const m = f.modal
        const me = modal.current!
        set(me, "visibility", m.visible ? "visible" : "hidden")
        if (m.visible) {
            set(me, "width", px(m.w))
            set(me, "height", px(m.h))
            set(me, "border-radius", px(m.r))
            set(me, "transform", `translate3d(-50%, -50%, 0) perspective(${m.persp.toFixed(1)}px) rotateY(${m.ry.toFixed(3)}deg) scale(${m.scale.toFixed(4)})`)
            set(me, "opacity", m.opacity.toFixed(3))
            set(modalIn.current!, "opacity", m.content.toFixed(3))
            set(modalIn.current!, "pointer-events", m.content > 0.9 ? "auto" : "none")
        }
        return f
    }, [])

    // The intro comes in line by line, as the headline on the home (the same
    // curve, frame by frame, a little quicker): each line on screen (however the
    // text wraps) rises from its own height and fades in, 400ms, 90ms after the
    // one before. It starts once the photo and the font are ready (at most 0.6s),
    // so no part of it plays while the page is still busy loading.
    React.useLayoutEffect(() => {
        const p = intro.current!
        const words = Array.from(p.querySelectorAll<HTMLElement>(".ab-w > span"))
        let line: number[] = []
        const lines = () => {
            let top = -Infinity,
                n = -1
            line = words.map((w) => {
                const el = w.parentElement as HTMLElement
                // (the version not shown has no lines)
                if (!el.offsetParent) return 0
                const t = el.getBoundingClientRect().top
                if (Math.abs(t - top) > 2) ((top = t), n++)
                return n
            })
        }
        lines()
        const draw = (t: number) =>
            words.forEach((w, i) => {
                const k = progress(t, 90 * line[i], 400, "out")
                w.style.transform = k < 1 ? `translateY(${100 * (1 - k)}%)` : ""
                w.style.opacity = String(k)
            })
        const reduced = matchMedia("(prefers-reduced-motion: reduce)").matches
        draw(reduced ? 1e9 : 0)
        let raf = 0,
            gone = false
        const img = root.current?.querySelector<HTMLImageElement>(".ab-photo")
        const ready = Promise.all([
            img?.decode?.().catch(() => {}),
            (document as any).fonts?.ready,
        ])
        // (never later than this, whatever is still loading)
        const latest = new Promise((r) => setTimeout(r, 600))
        if (!reduced)
            Promise.race([ready, latest]).then(() => {
                if (gone) return
                let t0 = 0
                const end = 90 * (Math.max(0, ...line) + 1) + 400
                const step = (now: number) => {
                    if (!t0) t0 = now
                    const t = now - t0
                    draw(t)
                    if (t < end) raf = requestAnimationFrame(step)
                }
                raf = requestAnimationFrame(step)
            })
        addEventListener("resize", lines)
        return () => {
            gone = true
            cancelAnimationFrame(raf)
            removeEventListener("resize", lines)
        }
    }, [])

    React.useLayoutEffect(() => {
        // ?t=3.2 shows one moment, still (for checking)
        const q = new URLSearchParams(location.search).get("t")
        if (q != null) {
            const time = Number(q)
            paint(time)
            const resize = () => paint(time)
            addEventListener("resize", resize)
            return () => removeEventListener("resize", resize)
        }
        // The time the scroll position asks for. With a finger it's shown as is
        // (the scrolling is already smooth and should stay under the finger);
        // with a mouse wheel the time glides after it, so its steps come out smooth.
        const touch = matchMedia("(pointer: coarse)").matches
        let max = 1
        const measure = () => (max = Math.max(1, document.documentElement.scrollHeight - innerHeight))
        measure()
        const target = () => Math.min(1, Math.max(0, scrollY / max)) * CARD.end
        let shown = target(),
            raf = 0,
            last = 0
        paint(shown)
        const step = (now: number) => {
            raf = 0
            const want = target()
            if (touch) {
                shown = want
                paint(shown)
                return
            }
            const dt = last ? Math.min(64, now - last) : 16.7
            last = now
            shown += (want - shown) * (1 - Math.pow(1 - 0.2, dt / 16.7))
            if (Math.abs(want - shown) < 0.0005) shown = want
            const f = paint(shown)
            const blurring = f ? motionBlur(f, dt / 1000) : false
            if (shown !== want || blurring) raf = requestAnimationFrame(step)
            else last = 0
        }
        // Desktop (the deck fanned out sideways): the same motion blur as the cards
        // on the home. Each card is blurred sideways by its own speed across the
        // screen (smoothed as there, rate 12/s): sigma = 0.012 x px/s, at most 18px.
        // On the faces (a filter on the card itself would flatten its two sides).
        const blur = cards.current.map(() => ({ speed: 0, x: NaN, value: "" }))
        const motionBlur = (f: ReturnType<typeof timelineFor>, dt: number) => {
            let moving = false
            f.cards.forEach((c, i) => {
                const b = blur[i]
                // (its speed on screen, as its camera shows it)
                const sx = c.cx + ((c.x - c.cx) * c.persp) / Math.max(1, c.persp - c.z)
                const v = f.axis === "x" && c.visible && !isNaN(b.x) && dt > 0 ? Math.abs(sx - b.x) / dt : 0
                b.x = sx
                b.speed += (Math.min(v * 0.012, 18) - b.speed) * (1 - Math.exp(-dt * 12))
                if (b.speed < 0.3 && v === 0) b.speed = 0
                const on = b.speed > 0.3
                if (on) moving = true
                const value = on ? `${b.speed.toFixed(2)} 0` : ""
                if (value === b.value) return
                b.value = value
                if (on) blurNodes.current[i]?.setAttribute("stdDeviation", value)
                motion.current[i] = on
                filterOf(i)
            })
            return moving
        }
        // (with a finger: drawn as soon as the page has scrolled, not a frame later)
        const go = () => (touch ? ((shown = target()), paint(shown)) : raf || (raf = requestAnimationFrame(step)))
        const resize = () => (measure(), paint(shown))
        addEventListener("scroll", go, { passive: true })
        addEventListener("resize", resize)
        // the page change out of here takes the scene on screen as its section
        const st = stage.current!
        Sections.set([st])
        return () => {
            cancelAnimationFrame(raf)
            removeEventListener("scroll", go)
            removeEventListener("resize", resize)
            Sections.clear([st])
        }
    }, [paint])

    return (
        <div ref={root} className="ab">
            <div ref={stage} className="ab-stage" data-nto-scene="">
                {use3d && <canvas ref={canvas} className="ab-gl" aria-hidden="true" />}
                {!use3d && deck.map((card, i) => (
                    <div
                        key={i}
                        ref={(el) => (cards.current[i] = el)}
                        className="ab-card"
                        style={{ ["--c" as any]: card.color, ["--ink" as any]: card.ink }}
                    >
                        <div className={"ab-face ab-front" + (card.photo ? " ab-front-photo" : variant === "deck" ? " hc" : "")}>
                            {card.photo && (
                                // filling the card, centred: full screen at first, then the card's crop
                                <img
                                    className="ab-photo"
                                    src={card.photo}
                                    srcSet={[800, 1200, 2000, 3364].map((w) => card.photo!.replace("3364", String(w)) + " " + w + "w").join(", ")}
                                    sizes="100vw"
                                    alt=""
                                    fetchPriority="high"
                                    decoding="async"
                                    onLoad={(e) => photoIn(e.currentTarget)}
                                    ref={(el) => el?.complete && el.naturalWidth > 0 && photoIn(el)}
                                />
                            )}
                            {variant === "deck" ? (
                                card.skill && <SkillFront skill={card.skill} />
                            ) : (
                                // (at the bottom: while the deck opens, the card above covers the top)
                                card.front && <Capability c={card.front} low />
                            )}
                        </div>
                        <div className={"ab-face ab-back" + (variant === "deck" ? " hc hc-back" : "")}>
                            {variant === "deck" ? <MarkBack /> : card.back && <Capability c={card.back} />}
                        </div>
                    </div>
                ))}
                <svg aria-hidden="true" width="0" height="0" className="ab-defs">
                    {deck.map((_, i) => (
                        <filter key={i} id={`ab-blur-${i}`} x="-25%" y="0%" width="150%" height="100%" colorInterpolationFilters="sRGB">
                            <feGaussianBlur ref={(el) => (blurNodes.current[i] = el)} stdDeviation="0 0" />
                        </filter>
                    ))}
                </svg>
                <div ref={intro as any} className="ab-intro">
                    {/* desktop and phone versions (their own line breaks); word by
                        word, so each line on screen can come in as one */}
                    <IntroText text={ABOUT.intro} className="ab-intro-d" />
                    <IntroText text={ABOUT.introPhone} className="ab-intro-m" />
                </div>
                <div ref={modal} className="ab-modal">
                    <div className="ab-face ab-modal-back" />
                    <div className="ab-face ab-modal-front">
                        <div ref={modalIn} className="ab-form-wrap">
                            <div className="ab-form-head">
                                <h2>{ABOUT.modal.title}</h2>
                                <p>{ABOUT.modal.text}</p>
                            </div>
                            {sent ? (
                                <p className="ab-sent">{ABOUT.modal.thanks}</p>
                            ) : (
                                <form
                                    className="ab-form"
                                    onSubmit={(e) => {
                                        e.preventDefault()
                                        setSent(true)
                                    }}
                                >
                                    <label>
                                        <span>Name</span>
                                        <input name="name" autoComplete="name" required />
                                    </label>
                                    <label>
                                        <span>Email</span>
                                        <input name="email" type="email" autoComplete="email" required />
                                    </label>
                                    <label className="ab-wide">
                                        <span>Message</span>
                                        <textarea name="message" rows={3} required />
                                    </label>
                                    <button type="submit">Send</button>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
